<template>
  <v-container fluid class="pa-4">
    <v-card>
      <v-card-title class="d-flex justify-space-between">
        <span class="title">{{ $t('title.controlPanel') }}</span>
        <div class="d-flex">
          <v-tooltip location="bottom" content-class="custom-tooltip">
            <template #activator="{ props }">
              <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="action-btn" icon="mdi-plus"
                @click="openAddDialog">
              </v-btn>
            </template>
            {{ $t('general.addControlPanelUser') }}
          </v-tooltip>
        </div>
      </v-card-title>

      <v-card-text>
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" class="mb-2" autocomplete="off"
          density="compact" />

        <v-data-table :headers="headers" :items="filteredUsers" :loading="loading" :loading-text="$t('general.loading')"
          class="min-width-table custom-header" v-model:sort-by="sortBy" v-model:sort-desc="sortDesc"
          :items-per-page="15">

          <!-- Bold naslovi i sort ikonice -->
          <template v-slot:headers="{ columns, toggleSort, isSorted, getSortIcon }">
            <tr>
              <th v-for="column in columns" :key="column.value" @click="() => column.sortable && toggleSort(column)"
                :style="{
                  cursor: column.sortable ? 'pointer' : 'default',
                  width: column.width,
                  maxWidth: column.width,
                  textAlign: column.value === 'actions' ? 'right' : 'left'
                }" class="sortable-header">
                <div class="d-flex align-center w-100" :class="column.value === 'actions' || column.value === 'Login'
                  ? 'justify-end'
                  : column.value === 'activation' || column.value === 'Login'
                    ? 'justify-center'
                    : 'justify-space-between'
                  ">
                  <strong>{{ column.text }}</strong>

                  <v-icon v-if="column.sortable" size="12" class="sort-icon ml-2" :class="{ active: isSorted(column) }">
                    {{ isSorted(column) ? getSortIcon(column) : 'mdi-arrow-up-down' }}
                  </v-icon>
                </div>
              </th>
            </tr>
          </template>

          <!-- Posljednje logovanje -->
          <template v-slot:[`item.Login`]="{ item }">
            <div style="text-align: right;">
              {{ formatLoginDate(item.Login) }}
            </div>
          </template>


          <!-- Aktivacija -->
          <template v-slot:[`item.activation`]="{ item }">
            <div class="activation-cell d-flex justify-center">
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" variant="text" density="compact" :ripple="false"
                    @click="toggleUserActivation(item)">
                    <img v-if="!!item.Active" :src="isCyrillic ? '/img/aktivanCir.png' : '/img/aktivanLat.png'"
                      height="22" />
                    <img v-if="!item.Active" :src="isCyrillic ? '/img/neaktivanCir.png' : '/img/neaktivanLat.png'"
                      height="22" />
                  </v-btn>
                </template>
                {{ item.Active
                  ? $t('table.controlPanel.deactivate')
                  : $t('table.controlPanel.activate') }}
              </v-tooltip>
            </div>
          </template>

          <!-- Akcije -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex justify-end action-buttons">
              <!-- POŠALJI ŠIFRU -->
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="green" class="mr-2 action-btn"
                    type="button" @click="sendPassword(item)">
                    <v-icon size="16">mdi-key</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.slanjeSifre') }}
              </v-tooltip>
              <!-- EDIT -->
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="primary" class="mr-2 action-btn"
                    type="button" @click="openEditDialog(item)">
                    <v-icon size="16">mdi-pencil</v-icon>
                  </v-btn>
                </template>

                {{ $t('general.uredi') }}
              </v-tooltip>
              <!-- POBRISI-->
              <v-tooltip location="bottom" content-class="custom-tooltip">
                <template #activator="{ props }">
                  <v-btn v-bind="props" size="small" variant="outlined" color="red" class="action-btn" type="button"
                    @click="openDeleteDialog(item)">
                    <v-icon size="16">mdi-delete</v-icon>
                  </v-btn>
                </template>
                {{ $t('general.delete') }}
              </v-tooltip>


            </div>
          </template>

        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Dijalog za dodavanje/uređivanje korisnika sistema -->
    <BaseModal v-model="dialog"
      :title="editMode ? this.$t('modalni.controlPanel.uredi') : this.$t('modalni.controlPanel.dodaj')">
      <v-form ref="controlPanelForm" v-model="isFormValid">
        <v-select v-model="form.employee" :items="employees" :label="$t('table.controlPanel.zaposlenik')"
          item-title="Name" item-value="UserId" :rules="[rules.required]" class="my-selectModal" :readonly="editMode" />

        <v-select v-model="form.roleId" :items="roles" :label="$t('table.controlPanel.role')" item-title="Name"
          item-value="Id" :rules="[rules.required]" class="my-selectModal" />

        <v-text-field v-model="form.username" :label="$t('table.controlPanel.username')" :rules="[rules.required]"
          class="my-text" required />

        <v-text-field v-model="form.email" :label="$t('table.controlPanel.email')" type="email"
          :rules="[rules.required, rules.email]" class="my-text" />
      </v-form>

      <template #footer>
        <div class="d-flex justify-end ga-2">

          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="closeDialog">
            <span style="font-size:14px; font-weight:600"> {{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- SAČUVAJ -->
          <v-btn color="primary" variant="flat" @click="submitSystemUser">
            <span style="font-size:14px; font-weight:600"> {{ $t('general.save') }}</span>
          </v-btn>
        </div>
      </template>

    </BaseModal>

    <!--  Dijalog za potvrdu brisanja -->
    <BaseModal v-model="deleteDialog" :title="this.$t('modalni.controlPanel.brisi')">
      <p>{{ this.$t('modalni.controlPanel.brisanjePoruka') }} <strong>{{ deleteUserName }}</strong>?</p>

      <template #footer>
        <div class="d-flex justify-end ga-2">
          <!-- OTKAŽI -->
          <v-btn variant="outlined" @click="deleteDialog = false">
            <span style="font-size:14px; font-weight: 600;">{{ $t('general.cancel') }}</span>
          </v-btn>

          <!-- OBRIŠI -->
          <v-btn color="danger" variant="flat" @click="confirmDelete">
            <span style="font-size:14px; font-weight: 600; color: #FFF">{{ $t('general.deleteButton') }}</span>
          </v-btn>
        </div>
      </template>
    </BaseModal>

  </v-container>
</template>

<script>

import BaseModal from "@/components/dialogs/BaseModal.vue";

export default {
  name: "ItUsersPage",
  components: { BaseModal },

  data() {
    return {
      originalUser: null,
      editMode: false,
      systemUsers: [],
      loading: false,
      search: "",
      sortBy: ["Name"],
      sortDesc: [false],
      dialog: false,
      employees: [],
      roles: [],
      isFormValid: false,
      form: { employee: null, roleId: null, email: "" },
      deleteDialog: false,
      deleteUserId: null,
      deleteUserName: "",

      rules: {
        required: v => (v !== null && v !== undefined && v !== '') || this.$t('systemMessage.m1'),
        email: (v) => !v || /.+@.+\..+/.test(v) || this.$t('systemMessage.m3')
      },

      headers: [
        { text: this.$t('table.controlPanel.zaposlenik'), value: "Name", sortable: true, width: "20%" },
        { text: this.$t('table.controlPanel.email'), value: "Email", sortable: true, width: "20%" },
        { text: this.$t('table.controlPanel.role'), value: "Role", sortable: true, width: "20%" },
        { text: this.$t('table.controlPanel.login'), value: "Login", sortable: true, width: "20%" },
        { text: this.$t('table.controlPanel.active'), align: "center", value: "activation", sortable: false, width: "20%-140px" },
        { text: this.$t('table.controlPanel.options'), value: "actions", sortable: false, width: '140px' },
      ],

    };
  },
  watch: {
    'form.employee'(val) {
      if (!val || this.editMode) return

      const employee = this.employees.find(e => e.UserId === val)

      if (employee && employee.Name) {
        this.form.username = this.generateUsername(employee.Name)
      }
      this.form.email = employee?.Email || ""
    }
  },

  computed: {
    isCyrillic() {
      return localStorage.getItem('LanguageId') === '1'
    },
    filteredUsers() {
      const term = (this.search || "").toLowerCase().trim()
      if (!term) return this.systemUsers

      return this.systemUsers.filter(u =>
        Object.values(u).some(val =>
          String(val ?? "").toLowerCase().includes(term)
        )
      )
    },

    generatedUsername() {
      if (!this.form.employee || !this.form.employee.Name) {
        return ''
      }
      return this.generateUsername(this.form.employee.Name)
    }
  },

  async mounted() {
    await Promise.all([
      this.fetchUsers(),
      this.fetchEmployees(),
      this.fetchRoles()
    ]);
  },

  methods: {
    formatLoginDate(value) {
      if (!value || value === "/" || value === null) return "—"

      const cleaned = value.replace(",", "")
      const date = new Date(cleaned.replace(" ", "T"))

      if (isNaN(date)) return value

      const day = String(date.getDate()).padStart(2, "0")
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const year = date.getFullYear()

      const hours = String(date.getHours()).padStart(2, "0")
      const minutes = String(date.getMinutes()).padStart(2, "0")

      return `${day}.${month}.${year}, ${hours}:${minutes}`
    },
    openEditDialog(user) {
      const employee = this.employees.find(e => e.Name === user.Name)

      if (!employee) {
        console.warn("Employee not found for:", user)
        return
      }

      this.originalUser = { ...user }

      this.form = {
        employee,
        roleId: Number(user.RoleId),
        username: user.UserName,
        email: user.Email || ""
      }

      this.editMode = true
      this.dialog = true

      this.$nextTick(() => {
        this.$refs.controlPanelForm?.resetValidation()
      })
    },
    async fetchUsers() {
      this.loading = true
      try {
        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post('User/ShowSystemUsers', data)

        this.systemUsers = r.data;

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.systemUsers = []
      } finally {
        this.loading = false
      }
    },

    async fetchEmployees() {
      this.loading = true
      try {

        var data = {
          Id: Number(localStorage.getItem('CompanyId')),
          LanguageId: Number(localStorage.getItem('LanguageId'))
        }

        const r = await this.$axios.post("User/Show", data)

        this.employees = r.data

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.employees = []
      } finally {
        this.loading = false
      }

    },

    async fetchRoles() {
      this.loading = true
      try {
        const r = await this.$axios.get("Role")

        this.roles = r.data.filter(role => role.Id === 2 || role.Id === 3)

      } catch (err) {
        console.error(this.$t('general.noData'), err)
        this.roles = []
      } finally {
        this.loading = false
      }
    },
    generateUsername(fullName) {
      return String(fullName || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim()
        .replace(/\s+/g, ".");
    },

    openAddDialog() {
      this.resetForm();
      this.originalUser = null
      this.editMode = false;
      this.dialog = true;

      this.$nextTick(() => {
        this.$refs.controlPanelForm?.resetValidation()
      })
    },
    async submitSystemUser() {
      const { valid } = await this.$refs.controlPanelForm.validate()
      if (!valid) return

      try {
        const employeeId = this.form.employee

        if (!employeeId) {
          throw new Error(this.$t("User_message.m80"))
        }

        const countryId = Number(localStorage.getItem('CountryId'))
        const countryLanguageMap = { 1: 2, 2: 3, 3: 4 }
        const languageId = countryLanguageMap[countryId] || 1

        let r

        if (!this.editMode) //add user
        {
         // const username = this.form.username
          r = await this.$axios.post(`User/AddUserActivation`, {
            UserId: employeeId,
            RoleId: this.form.roleId,
            UserName: this.form.username,
            Email: this.form.email,
            CountryId: countryId,
            LanguageId: languageId
          })
        }
        else //update user
        {
          r = await this.$axios.post(`User/AddUserActivation`, {
            UserId: employeeId.UserId,
            RoleId: this.form.roleId,
            UserName: this.form.username,
            Email: this.form.email,
            CompanyId: Number(localStorage.getItem("CompanyId"))
          })
        }

        /*
        if (this.editMode) {
          //r = await this.$axios.put("User/ChangeRole", {
          r = await this.$axios.post(`User/AddUserActivation`, {
            UserId: employeeId.UserId,
            RoleId: this.form.roleId,
            UserName: this.form.username,
            Email: this.form.email,
            CompanyId: Number(localStorage.getItem("CompanyId"))
          })
        }
        */
       if (r.data === "EXCISTED EMAIL") 
       {
          this.$store.dispatch("NOTIF_WARNING", {
            poruka: this.$t("User_message.m152"),
            position: "center",
            icon: "error",
            timer: 4500,
            width: "550"
          })
          return
        }

        if (r.data === "EXCISTED USERNAME") 
        {
          this.$store.dispatch("NOTIF_WARNING", {
            poruka: this.$t("User_message.m153"),
            position: "center",
            icon: "error",
            timer: 4500,
            width: "550"
          })
          return
        }

        this.$store.dispatch("NOTIF_SUCCESS", {
          poruka: this.editMode
            ? this.$t("User_message.m74")
            : this.$t("User_message.m75"),
          position: "top-end",
          timer: 2500,
          action: "noreload"
        })

        this.dialog = false
        this.resetForm()
        this.editMode = false
        await this.fetchUsers()

      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: err?.response?.data || err.message || this.$t("User_message.m76"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        })
      }
    },

    async toggleUserActivation(user) {
      try {
        await this.$axios.put('User/ChangeActive/' + user.UserId);
        await this.fetchUsers();
      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t("User_message.m77"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        });
      }
    },

    openDeleteDialog(user) {
      this.deleteUserId = user.UserId;
      this.deleteUserName = user.Name;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      if (!this.deleteUserId) return;

      try {
        await this.$axios.put(
          'User/DeleteSystemUsers/' + this.deleteUserId
        );

        this.$store.dispatch("NOTIF_SUCCESS", {
          poruka: this.$t("User_message.m78"),
          position: "top-end",
          timer: 2500,
          action: "noreload",
        });

        await this.fetchUsers();

      } catch (err) {
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t("User_message.m79"),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        });
      } finally {
        this.deleteDialog = false;
        this.deleteUserId = null;
        this.deleteUserName = "";
      }
    },
    closeDialog() {
      this.dialog = false;
      this.resetForm();

      this.$nextTick(() => {
        this.$refs.controlPanelForm?.resetValidation()
      })
    },
    resetForm() {
      this.form = { employee: null, roleId: null, username: "", email: "" };
    },
    async sendPassword(value) {
      try {
        const response = await this.$axios.put('User/SendPassword', {
          Id: value.UserId,
          CompanyId: Number(localStorage.getItem("CompanyId"))
        });

        if (response.status === 200) {
          // Uspješna promjena šifre
          this.$store.dispatch("NOTIF_SUCCESS", {
            poruka: this.$t('general.sendPasswordReset'),
            position: "top-end",
            timer: 2500,
            action: "noreload"
          });
        }
      } catch (err) {
        // Greška prilikom slanja šifre
        this.$store.dispatch("NOTIF_WARNING", {
          poruka: this.$t('general.passwordResetError'),
          position: "center",
          icon: "error",
          timer: 4500,
          width: "550"
        });
      }
    }
  }
};
</script>

<style scoped>
.activation-btn {
  width: 110px;
  height: 28px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  text-transform: none;
}
</style>
